import React, { useEffect, useState } from 'react';
import { X, Save, CreditCard } from 'lucide-react';
import { PaymentType } from '../../types/payment';
import { alertService } from '../../services/alertService';
import { paymentTypeService } from '../../services/paymentTypeService';

interface PaymentTypeModalProps {
  paymentType?: PaymentType | null;
  onClose: () => void;
  onSave?: (paymentType: PaymentType) => void;
}

const PaymentTypeModal: React.FC<PaymentTypeModalProps> = ({ paymentType, onClose, onSave }) => {
  const [formData, setFormData] = useState<Omit<PaymentType, 'id'>>({
    nome: '',
    descricao: '',
    ativo: true,
    ordem: undefined,
    pagamentoImediato: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (paymentType) {
      setFormData({
        nome: paymentType.nome,
        descricao: paymentType.descricao || '',
        ativo: paymentType.ativo ?? true,
        ordem: paymentType.ordem,
        pagamentoImediato: !!paymentType.pagamentoImediato,
      });
    }
  }, [paymentType]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.nome.trim()) newErrors.nome = 'Nome é obrigatório';
    if (formData.descricao && formData.descricao.length > 500) newErrors.descricao = 'Descrição deve ter no máximo 500 caracteres';
    if (formData.ordem !== undefined && (isNaN(Number(formData.ordem)) || Number(formData.ordem) < 0)) newErrors.ordem = 'Ordem deve ser um número maior ou igual a 0';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : name === 'ordem' ? (value === '' ? undefined : Number(value)) : value,
    }));
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      let response;
      if (paymentType) {
        response = await paymentTypeService.update(paymentType.id, {
          nome: formData.nome.trim(),
          descricao: formData.descricao?.trim() || undefined,
          ativo: formData.ativo,
          ordem: formData.ordem,
          pagamentoImediato: !!formData.pagamentoImediato,
        });
        alertService.success('Tipo de pagamento atualizado com sucesso!');
      } else {
        response = await paymentTypeService.create({
          nome: formData.nome.trim(),
          descricao: formData.descricao?.trim() || undefined,
          ativo: formData.ativo,
          ordem: formData.ordem,
          pagamentoImediato: !!formData.pagamentoImediato,
        });
        alertService.success('Tipo de pagamento criado com sucesso!');
      }
      if (response.data && onSave) onSave(response.data);
      onClose();
    } catch (error: any) {
      console.error('Erro ao salvar tipo de pagamento:', error);
      if (error.response?.data?.message) {
        alertService.error(error.response.data.message);
      } else {
        alertService.error('Erro ao salvar tipo de pagamento');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-white flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-2 sm:gap-3">
            <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              {paymentType ? 'Editar Tipo de Pagamento' : 'Novo Tipo de Pagamento'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Dados Básicos</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        errors.nome ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Digite o nome do tipo de pagamento"
                      maxLength={100}
                    />
                    {errors.nome && (<p className="mt-1 text-sm text-red-600">{errors.nome}</p>)}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ordem</label>
                    <input
                      type="number"
                      name="ordem"
                      value={formData.ordem ?? ''}
                      onChange={handleChange}
                      min={0}
                      step={1}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        errors.ordem ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Informe a ordem de exibição"
                    />
                    {errors.ordem && (<p className="mt-1 text-sm text-red-600">{errors.ordem}</p>)}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                    <textarea
                      name="descricao"
                      value={formData.descricao || ''}
                      onChange={handleChange}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        errors.descricao ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Digite uma descrição"
                      maxLength={500}
                    />
                    {errors.descricao && (
                      <p className="mt-1 text-sm text-red-600">{errors.descricao}</p>
                    )}
                  </div>

              <div>
                <label className="inline-flex items-center me-5 cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={formData.ativo}
                    onChange={() =>
                      setFormData(prev => ({
                        ...prev, ativo: !prev.ativo
                      }))
                    }
                  />

                  <div className="
                      relative w-11 h-6 rounded-full
                      transition-colors
                      peer-focus:ring-4 peer-focus:ring-red-300
                      bg-gray-200 peer-checked:bg-red-600
                      after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                      after:h-5 after:w-5 after:bg-white after:rounded-full after:transition-all
                      peer-checked:after:translate-x-5
                    ">
                  </div>

                  <span className="select-none ms-3 text-sm font-medium text-heading">
                    {formData.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </label>
              </div>

              <div>
                <label className="inline-flex items-center me-5 cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={formData.pagamentoImediato}
                    onChange={() =>
                      setFormData(prev => ({
                        ...prev,
                        pagamentoImediato: !prev.pagamentoImediato
                      }))
                    }
                  />

                  <div className="
                      relative w-11 h-6 rounded-full
                      transition-colors
                      peer-focus:ring-4 peer-focus:ring-red-300
                      bg-gray-200 peer-checked:bg-red-600
                      after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                      after:h-5 after:w-5 after:bg-white after:rounded-full after:transition-all
                      peer-checked:after:translate-x-5
                    ">
                  </div>

                  <span className="select-none ms-3 text-sm font-medium text-heading">
                    {formData.pagamentoImediato ? "Pagamento Imediato (SIM)" : "Pagamento Imediato (Não)"}
                  </span>
                </label>
              </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 bg-gray-50 p-4 sm:p-6 border-t border-gray-200 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 disabled:opacity-70">
              <Save className="h-4 w-4" />
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentTypeModal;